import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Search, 
  MapPin, 
  Star, 
  Heart, 
  SlidersHorizontal,
  Grid3X3,
  Map,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  List,
  Building2
} from "lucide-react";
import { BusinessCardPhoto } from "@/components/BusinessCardPhoto";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { BusinessMapView } from "@/components/maps/BusinessMapView";
import { SEOHead } from "@/components/seo/SEOHead";
import { AdSenseUnit } from "@/components/ads/AdSenseUnit";


const categories = [
  "All",
  "Massage",
  "Spa",
  "Barber",
  "Beauty",
  "Skin Care",
  "Fitness",
  "Sports Recovery",
  "Nail Spa",
];

const categoryMap: Record<string, string> = {
  "All": "All",
  "Massage": "massage_therapy",
  "Spa": "spa_wellness",
  "Barber": "barber_shop_grooming",
  "Beauty": "beauty_salon",
  "Skin Care": "skin_care_aesthetics",
  "Fitness": "fitness_personal_training",
  "Sports Recovery": "sports_recovery_rehabilitation",
  "Nail Spa": "nail_spa",
};

const categoryLabels: Record<string, string> = {
  massage_therapy: "Massage Therapy",
  spa_wellness: "Spa & Wellness",
  barber_shop_grooming: "Barber Shop & Grooming",
  beauty_salon: "Beauty Salon",
  skin_care_aesthetics: "Skin Care & Aesthetics",
  fitness_personal_training: "Fitness & Personal Training",
  sports_recovery_rehabilitation: "Sports Recovery & Rehabilitation",
  nail_spa: "Nail Spa",
};

interface Business {
  id: string;
  business_name: string;
  category: string;
  address: string;
  state?: string | null;
  postcode?: string | null;
  latitude: number | null;
  longitude: number | null;
  description: string | null;
}

const ITEMS_PER_PAGE = 12;

export default function Browse() {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [locationQuery, setLocationQuery] = useState(searchParams.get("location") || "");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [viewMode, setViewMode] = useState<"list" | "map">("list");
  const [showFilters, setShowFilters] = useState(false);
  const [showMapPreview, setShowMapPreview] = useState(false);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const category = searchParams.get("category");
    if (category) {
      const matchedCategory = Object.entries(categoryMap).find(
        ([_, value]) => value === category
      );
      if (matchedCategory) {
        setSelectedCategory(matchedCategory[0]);
      }
    }
  }, [searchParams]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, locationQuery, selectedCategory]);

  useEffect(() => {
    async function fetchBusinesses() {
      setLoading(true);
      try {
        // Build query with pagination - view already filters for active/trial and not suspended
        let query = supabase
          .from("businesses_public" as any)
          .select("id, business_name, category, address, state, postcode, latitude, longitude, description", { count: "exact" });

        // Apply category filter at database level for efficiency
        if (selectedCategory !== "All" && categoryMap[selectedCategory]) {
          query = query.eq("category", categoryMap[selectedCategory]);
        }

        // Get paginated results
        const from = (currentPage - 1) * ITEMS_PER_PAGE;
        const to = from + ITEMS_PER_PAGE - 1;
        
        const { data: businessesData, error: businessesError, count } = await query
          .range(from, to)
          .order("business_name") as { data: Business[] | null; error: any; count: number | null };

        if (businessesError) throw businessesError;

        setBusinesses(businessesData || []);
        setTotalCount(count || 0);
      } catch (error) {
        console.error("Error fetching businesses:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchBusinesses();
  }, [currentPage, selectedCategory]);

  // Client-side filtering for search and location (already paginated by category at DB level)
  const filteredBusinesses = businesses.filter((business) => {
    const matchesSearch = !searchQuery || 
      business.business_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (business.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);
    const matchesLocation = !locationQuery || 
      business.address.toLowerCase().includes(locationQuery.toLowerCase());
    return matchesSearch && matchesLocation;
  });

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getCategoryTitle = () => {
    if (selectedCategory !== "All") return `${selectedCategory} Services`;
    return "Wellness Services";
  };

  const getSEODescription = () => {
    if (selectedCategory !== "All") {
      return `Find and book the best ${selectedCategory.toLowerCase()} services worldwide. Browse wellness practitioners, compare prices, read customer reviews, and book ${selectedCategory.toLowerCase()} appointments online. Pay securely with debit or credit card.`;
    }
    return "Discover health and wellness services worldwide. Book spa treatments, massage therapy, yoga classes, fitness training, and holistic health practitioners online. Find wellness clinics near you in New York, London, Tokyo, and worldwide.";
  };

  const getSEOKeywords = () => {
    const baseKeywords = "wellness globally, health and wellness services, online wellness booking";
    if (selectedCategory !== "All") {
      return `${selectedCategory.toLowerCase()} globally, ${selectedCategory.toLowerCase()} near me, ${selectedCategory.toLowerCase()} Sydney, ${selectedCategory.toLowerCase()} Melbourne, book ${selectedCategory.toLowerCase()} online, ${baseKeywords}`;
    }
    return `${baseKeywords}, wellness clinics near me, spa globally, massage therapy Sydney, yoga meditation Melbourne, fitness trainers Brisbane, holistic health practitioners`;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title={`Browse ${getCategoryTitle()} worldwide - Book Online`}
        description={getSEODescription()}
        keywords={getSEOKeywords()}
        canonicalUrl="/browse"
      />
      <Navbar />
      
      <main className="flex-1 pt-16 md:pt-20">
        {/* Search Header - Sticky on mobile */}
        <div className="bg-card border-b border-border sticky top-16 md:top-20 z-40">
          <div className="container mx-auto px-4 py-3 md:py-4">
            {/* Mobile: Compact search */}
            <div className="flex gap-2 md:hidden">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-12 pl-10 pr-4 rounded-xl bg-secondary/50 border border-border text-foreground text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              
              {/* Filter Button */}
              <Sheet open={showFilters} onOpenChange={setShowFilters}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="h-12 w-12 shrink-0">
                    <SlidersHorizontal className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-[70vh] rounded-t-3xl">
                  <SheetHeader>
                    <SheetTitle>Filters</SheetTitle>
                  </SheetHeader>
                  <div className="py-6 space-y-6">
                    <div>
                      <h3 className="font-medium text-foreground mb-3">Category</h3>
                      <div className="flex flex-wrap gap-2">
                        {categories.map((category) => (
                          <button
                            key={category}
                            onClick={() => {
                              setSelectedCategory(category);
                              setShowFilters(false);
                            }}
                            className={`px-4 py-3 rounded-xl text-sm font-medium transition-all touch-manipulation ${
                              selectedCategory === category
                                ? "gradient-primary text-primary-foreground"
                                : "bg-secondary text-secondary-foreground"
                            }`}
                          >
                            {category}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground mb-3">Sort by</h3>
                      <div className="space-y-2">
                        {["Recommended", "Highest Rated", "Nearest", "Price: Low to High"].map((option) => (
                          <button
                            key={option}
                            className="w-full px-4 py-3 rounded-xl text-left text-sm font-medium bg-secondary hover:bg-secondary/80 transition-colors touch-manipulation"
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>

              {/* View Toggle */}
              <div className="flex rounded-xl border border-border overflow-hidden shrink-0">
                <Button
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="icon"
                  className="h-12 w-12 rounded-none"
                  onClick={() => setViewMode("list")}
                >
                  <List className="w-5 h-5" />
                </Button>
                <Button
                  variant={viewMode === "map" ? "default" : "ghost"}
                  size="icon"
                  className="h-12 w-12 rounded-none"
                  onClick={() => setViewMode("map")}
                >
                  <Map className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Desktop: Full search bar */}
            <div className="hidden md:flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search services, spas, or practitioners..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-12 pl-12 pr-4 rounded-xl bg-secondary/50 border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div className="flex-1 relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Location"
                  value={locationQuery}
                  onChange={(e) => setLocationQuery(e.target.value)}
                  className="w-full h-12 pl-12 pr-4 rounded-xl bg-secondary/50 border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <Button 
                variant="outline" 
                className="h-12"
                onClick={() => setShowFilters(!showFilters)}
              >
                <SlidersHorizontal className="w-5 h-5 mr-2" />
                Filters
              </Button>
              <div className="flex gap-1 p-1 bg-secondary rounded-xl border border-border">
                <Button
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="icon"
                  onClick={() => setViewMode("list")}
                >
                  <Grid3X3 className="w-5 h-5" />
                </Button>
                <Button
                  variant={viewMode === "map" ? "default" : "ghost"}
                  size="icon"
                  onClick={() => setViewMode("map")}
                >
                  <Map className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Categories - Horizontal scroll on mobile */}
          <div className="border-t border-border">
            <div className="container mx-auto px-4">
              <div className="flex gap-2 py-3 overflow-x-auto scrollbar-none -mx-4 px-4">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all touch-manipulation active:scale-95 ${
                      selectedCategory === category
                        ? "gradient-primary text-primary-foreground shadow-wellness-sm"
                        : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="container mx-auto px-4 py-4 md:py-8">
          <div className="flex justify-between items-center mb-4 md:mb-6">
            <p className="text-sm md:text-base text-muted-foreground">
              <span className="font-medium text-foreground">{filteredBusinesses.length}</span> results
            </p>
            <select className="hidden md:block px-4 py-2 rounded-lg bg-card border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50">
              <option>Sort by: Recommended</option>
              <option>Highest Rated</option>
              <option>Nearest</option>
              <option>Price: Low to High</option>
            </select>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="overflow-hidden animate-pulse">
                  <div className="h-48 bg-muted" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </Card>
              ))}
            </div>
          ) : viewMode === "list" ? (
            filteredBusinesses.length === 0 ? (
              <div className="text-center py-16">
                <Building2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                  No businesses found
                </h3>
                <p className="text-muted-foreground">
                  Try adjusting your search or filters
                </p>
              </div>
            ) : (
              <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {filteredBusinesses.map((business, index) => (
                  <Link
                    key={business.id}
                    to={`/provider/${business.id}`}
                    className="group block touch-manipulation"
                  >
                    <Card 
                      variant="elevated" 
                      className="overflow-hidden h-full animate-fade-up active:scale-[0.98] transition-transform"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      {/* Business Photo */}
                      <div className="relative">
                        <BusinessCardPhoto businessId={business.id} businessName={business.business_name} />
                        
                        {/* Badges */}
                        <div className="absolute top-3 left-3 right-3 flex justify-between">
                          <Badge variant="secondary" className="bg-primary/90 text-primary-foreground border-0 text-xs">
                            {categoryLabels[business.category] || business.category}
                          </Badge>
                          <button 
                            className="w-10 h-10 rounded-full bg-card/80 backdrop-blur-sm flex items-center justify-center hover:bg-card transition-colors touch-manipulation"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <Heart className="w-5 h-5 text-muted-foreground" />
                          </button>
                        </div>

                        {/* Location indicator */}
                        {business.latitude && business.longitude && (
                          <div className="absolute bottom-3 right-3">
                            <Badge variant="secondary" className="bg-sky-500/90 text-primary-foreground border-0 text-xs">
                              <MapPin className="w-3 h-3 mr-1" />
                              On Map
                            </Badge>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-4 md:p-5">
                        <h3 className="font-display text-base md:text-lg font-semibold text-foreground mb-1.5 group-hover:text-primary transition-colors line-clamp-1">
                          {business.business_name}
                        </h3>

                        {/* Location */}
                        <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3">
                          <MapPin className="w-4 h-4 shrink-0" />
                          <span className="truncate">{[business.address, business.state, business.postcode].filter(Boolean).join(', ')}</span>
                        </div>

                        {/* Description */}
                        {business.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {business.description}
                          </p>
                        )}
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8 pb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {/* Show page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          className="w-10 h-10"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="gap-1"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {/* Results summary */}
              <p className="text-center text-sm text-muted-foreground mt-4">
                Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount} businesses
              </p>
              </>
            )
          ) : (
            <div className="relative h-[calc(100vh-220px)] md:h-[600px] overflow-hidden">
              <BusinessMapView 
                businesses={filteredBusinesses}
                className="h-full"
              />
              
              {/* Mobile: Bottom sheet preview of providers */}
              <div className="absolute bottom-0 left-0 right-0 md:hidden">
                <button
                  onClick={() => setShowMapPreview(!showMapPreview)}
                  className="w-full py-3 bg-card border-t border-border flex items-center justify-center gap-2 touch-manipulation"
                >
                  <ChevronUp className={`w-5 h-5 transition-transform ${showMapPreview ? "rotate-180" : ""}`} />
                  <span className="font-medium">{filteredBusinesses.length} places</span>
                </button>
                
                {showMapPreview && (
                  <div className="bg-card max-h-64 overflow-y-auto">
                    {filteredBusinesses.map((business) => (
                      <Link
                        key={business.id}
                        to={`/provider/${business.id}`}
                        className="flex items-center gap-3 p-4 border-t border-border touch-manipulation active:bg-secondary"
                      >
                        <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                          <Building2 className="w-8 h-8 text-primary/50" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-foreground truncate">{business.business_name}</h3>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <MapPin className="w-3 h-3" />
                            <span className="truncate">{[business.address, business.state, business.postcode].filter(Boolean).join(', ')}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <AdSenseUnit />
      <Footer />
    </div>
  );
}
